import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Search } from 'lucide-react';

export interface RenewalTaskFilters {
  range?: 'day' | 'week' | 'month' | 'quarter';
  status?: 'OPEN' | 'IN_PROGRESS' | 'WAITING' | 'RESOLVED' | 'CLOSED' | 'VALID' | 'EXPIRED' | 'PENDING';
  priority?: 'P0' | 'P1' | 'P2' | 'P3' | 'P4';
  bucket?: 'OVERDUE' | 'D0_3' | 'D4_7' | 'D8_14' | 'D15_30' | 'D31_90' | 'D90_PLUS';
  clientId?: string;
  ownerUserId?: string;
  clientSearch?: string;
}

interface RenewalTaskFiltersProps {
  filters: RenewalTaskFilters;
  onFiltersChange: (filters: RenewalTaskFilters) => void;
  clients?: Array<{ id: string; name: string }>;
  staffMembers?: Array<{ id: string; label: string }>;
}

export const RenewalTaskFiltersComponent: React.FC<RenewalTaskFiltersProps> = ({
  filters,
  onFiltersChange,
  clients = [],
  staffMembers = [],
}) => {
  const [clientSearchTerm, setClientSearchTerm] = useState(filters.clientSearch || '');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local state with filters prop
  useEffect(() => {
    setClientSearchTerm(filters.clientSearch || '');
  }, [filters.clientSearch]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleFilterChange = (key: keyof RenewalTaskFilters, value: string | undefined) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined,
    });
  };

  const handleClientSearchChange = (value: string) => {
    setClientSearchTerm(value);
    
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Debounce API call by 500ms
    debounceTimerRef.current = setTimeout(() => {
      onFiltersChange({
        ...filters,
        clientSearch: value.trim() || undefined,
      });
    }, 500);
  };

  const handleClientSearchSubmit = (value: string) => {
    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Immediately trigger search
    setClientSearchTerm(value);
    onFiltersChange({
      ...filters,
      clientSearch: value.trim() || undefined,
    });
  };

  const clearFilters = () => {
    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setClientSearchTerm('');
    onFiltersChange({});
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== undefined && v !== '');

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-3 w-full">
            {/* Status Filter */}
            <div className="flex flex-col gap-1 min-w-[140px]">
              <Label htmlFor="status" className="text-xs">Status</Label>
              <Select
                value={filters.status || 'all'}
                onValueChange={(value) => handleFilterChange('status', value === 'all' ? undefined : value as any)}
              >
                <SelectTrigger id="status" className="h-9">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="VALID">Valid</SelectItem>
                  <SelectItem value="EXPIRED">Expired</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Priority Filter */}
            <div className="flex flex-col gap-1 min-w-[140px]">
              <Label htmlFor="priority" className="text-xs">Priority</Label>
              <Select
                value={filters.priority || 'all'}
                onValueChange={(value) => handleFilterChange('priority', value === 'all' ? undefined : value as any)}
              >
                <SelectTrigger id="priority" className="h-9">
                  <SelectValue placeholder="All priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priorities</SelectItem>
                  <SelectItem value="P0">P0 - Critical</SelectItem>
                  <SelectItem value="P1">P1 - High</SelectItem>
                  <SelectItem value="P2">P2 - Medium</SelectItem>
                  <SelectItem value="P3">P3 - Low</SelectItem>
                  <SelectItem value="P4">P4 - Lowest</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bucket Filter */}
            <div className="flex flex-col gap-1 min-w-[160px]">
              <Label htmlFor="bucket" className="text-xs">Due Date Bucket</Label>
              <Select
                value={filters.bucket || 'all'}
                onValueChange={(value) => handleFilterChange('bucket', value === 'all' ? undefined : value as any)}
              >
                <SelectTrigger id="bucket" className="h-9">
                  <SelectValue placeholder="All buckets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All buckets</SelectItem>
                  <SelectItem value="OVERDUE">Overdue</SelectItem>
                  <SelectItem value="D0_3">Due in 0-3 days</SelectItem>
                  <SelectItem value="D4_7">Due in 4-7 days</SelectItem>
                  <SelectItem value="D8_14">Due in 8-14 days</SelectItem>
                  <SelectItem value="D15_30">Due in 15-30 days</SelectItem>
                  <SelectItem value="D31_90">Due in 31-90 days</SelectItem>
                  <SelectItem value="D90_PLUS">Due in 90+ days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Staff Filter */}
            {staffMembers.length > 0 && (
              <div className="flex flex-col gap-1 min-w-[140px]">
                <Label htmlFor="staff" className="text-xs">Staff</Label>
                <Select
                  value={filters.ownerUserId || 'all'}
                  onValueChange={(value) => handleFilterChange('ownerUserId', value === 'all' ? undefined : value)}
                >
                  <SelectTrigger id="staff" className="h-9">
                    <SelectValue placeholder="All staff" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All staff</SelectItem>
                    {staffMembers.map((staff) => (
                      <SelectItem key={staff.id} value={staff.id}>
                        {staff.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Client Search */}
            {clients.length > 0 && (
              <div className="flex flex-col gap-1 min-w-[200px]">
                <Label htmlFor="client-search" className="text-xs">Search Client</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="client-search"
                    type="text"
                    placeholder="Search by client name..."
                    value={clientSearchTerm}
                    onChange={(e) => handleClientSearchChange(e.target.value)}
                    onBlur={(e) => handleClientSearchSubmit(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleClientSearchSubmit(e.currentTarget.value);
                      }
                    }}
                    className="h-9 pl-8"
                  />
                </div>
              </div>
            )}

            {/* Clear Filters Button - Right aligned */}
            {hasActiveFilters && (
              <div className="ml-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="h-9 text-xs"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear
                </Button>
              </div>
            )}
          </div>
      </CardContent>
    </Card>
  );
};

